import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { confirmPopup } from 'components/Popups/Confirm';

import {
    useGetDeliveryZoneQuery,
    useGetDeliveryPeopleQuery,
    useUpdateDeliveryZoneMutation,
    useAssignSitesToDeliveryZoneMutation,
    DeliveryZoneState,
    TotadminPermission,
} from 'data/__generated__';

import { PAGES } from 'constants/pages';

import { DataTable } from 'components/DataTable';
import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { Loader } from 'components/Loader';
import { SectionContainer } from 'components/DetailsView/Section';
import { TotemInput } from 'components/TotemInput';
import { TotemSelect } from 'components/TotemSelect';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { zoneSitesColumns, type ZoneSiteRow } from '../constants/zoneSitesColumns';
import { AddSitesSection } from './AddSitesSection';
import { TourOrderSection } from './TourOrderSection';

type ParamTypes = {
    deliveryZoneId: string;
};

const ACTION_GROUPS: PageActionGroup[] = [
    {
        label: 'Navigation',
        items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/delivery-zones' }],
    },
];

export const DeliveryZoneDetails = () => {
    const { deliveryZoneId = '' } = useParams<ParamTypes>();

    const { loading, data, error, refetch } = useGetDeliveryZoneQuery({ variables: { _id: deliveryZoneId } });
    const { data: deliveryPeopleData } = useGetDeliveryPeopleQuery();
    const [updateDeliveryZone, { loading: updateLoading }] = useUpdateDeliveryZoneMutation();
    const [assignSitesToDeliveryZone, { loading: assignLoading }] = useAssignSitesToDeliveryZoneMutation();

    const [name, setName] = useState<string>('');
    const [color, setColor] = useState<string>('#3B82F6');
    const [referentDelivererId, setReferentDelivererId] = useState<string | null>(null);
    const [state, setState] = useState<DeliveryZoneState>(DeliveryZoneState.Regular);
    const [zoneSitesSelected, setZoneSitesSelected] = useState<string[]>([]);

    const zone = data?.deliveryZone;

    useEffect(() => {
        if (zone) {
            setName(zone.name);
            setColor(zone.color);
            setReferentDelivererId(zone.referentDelivererId ?? null);
            setState(zone.state);
        }
    }, [zone]);

    const deliveryPeopleOptions = useMemo(
        () =>
            (deliveryPeopleData?.getDeliveryPeople ?? [])
                .map(({ _id, profile: { fullname }, username }) => ({
                    value: _id,
                    label: `${fullname} (${username})`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [deliveryPeopleData],
    );

    // Memoized: hands a stable array ref to the zone DataTable and to the « Ajouter des sites » filter.
    const zoneSites = useMemo<ZoneSiteRow[]>(() => zone?.sites ?? [], [zone]);

    if (loading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (error || !zone) {
        throw new Error('Une erreur est survenue lors de la récupération de la zone');
    }

    const handleSave = async () => {
        try {
            await updateDeliveryZone({
                variables: { _id: deliveryZoneId, input: { name, color, referentDelivererId, state } },
            });
            toast.success('Zone mise à jour');
            await refetch();
        } catch {
            toast.error('Une erreur est survenue lors de la mise à jour de la zone');
        }
    };

    const handleRemoveSelected = async () => {
        if (zoneSitesSelected.length === 0) return;
        const confirmed = await confirmPopup({
            title: 'Retirer des sites',
            message: `Retirer ${zoneSitesSelected.length} site(s) de cette zone ?`,
        });
        if (!confirmed) return;
        try {
            // Le catalogue de « Ajouter des sites » affiche la zone actuelle de chaque site : rafraîchi s'il est
            // monté (refetch par nom = requêtes actives seulement).
            await assignSitesToDeliveryZone({
                variables: { deliveryZoneId: null, siteIds: zoneSitesSelected },
                refetchQueries: ['GET_SITES'],
                awaitRefetchQueries: true,
            });
            toast.success(`${zoneSitesSelected.length} site(s) retiré(s) de la zone`);
            setZoneSitesSelected([]);
            await refetch();
        } catch {
            toast.error('Une erreur est survenue lors du retrait des sites');
        }
    };

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.deliveryZoneDetails} />
                </HeaderTitle>
                <ActionsAndSave
                    actionGroups={ACTION_GROUPS}
                    onSave={handleSave}
                    saveDisabled={!name || !color}
                    saveLoading={updateLoading}
                    saveTitle="Enregistrer"
                    saveRequiredPermissions={[TotadminPermission.DeliveryZonesManage]}
                />
            </Header>
            <ScrollableContent>
                <SectionContainer title="Informations" isInitiallyOpen>
                    <Fields>
                        <TotemInput label="Nom de la zone" value={name} onChange={setName} />
                        <ColorField>
                            <ColorLabel>Couleur</ColorLabel>
                            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
                        </ColorField>
                        <TotemSelect
                            label="Appro référent"
                            placeholder="Sélectionner un livreur"
                            isClearable
                            value={referentDelivererId}
                            options={deliveryPeopleOptions}
                            onChange={setReferentDelivererId}
                        />
                        <TotemSelect
                            label="Statut"
                            value={state}
                            options={[
                                { value: DeliveryZoneState.Regular, label: 'Regular' },
                                { value: DeliveryZoneState.Archived, label: 'Archivé' },
                            ]}
                            onChange={(value) => setState(value ?? DeliveryZoneState.Regular)}
                        />
                    </Fields>
                </SectionContainer>

                <SectionContainer title={`Sites de la zone (${zoneSites.length})`} isInitiallyOpen>
                    <TableWrapper>
                        <DataTable<ZoneSiteRow>
                            columns={zoneSitesColumns}
                            data={zoneSites}
                            filterMode="client"
                            disableUrlSync
                            getRowId={(row) => row._id}
                            getRowLink={(row) => `/site/${row._id}`}
                            searchPlaceholder="Nom du site ou de l'organisation"
                            autoFocusSearch={false}
                            enableSelection
                            selectedKeys={zoneSitesSelected}
                            onSelectionChange={setZoneSitesSelected}
                            emptyMessage="Aucun site dans cette zone pour le moment."
                            toolbarActions={
                                zoneSitesSelected.length > 0 ? (
                                    <TotemPrimaryButton
                                        onClick={handleRemoveSelected}
                                        disabled={assignLoading}
                                        requiredPermissions={[TotadminPermission.DeliveryZonesManage]}
                                    >
                                        Retirer de la zone ({zoneSitesSelected.length})
                                    </TotemPrimaryButton>
                                ) : null
                            }
                        />
                    </TableWrapper>
                </SectionContainer>

                <TourOrderSection sites={zoneSites} />

                <AddSitesSection deliveryZoneId={deliveryZoneId} zoneSites={zoneSites} onSitesAssigned={refetch} />
            </ScrollableContent>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    flex: 1;
    min-height: 0;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;

const ColorField = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const ColorLabel = styled.span`
    color: ${({ theme }) => theme.textColor};
    font-weight: 600;
`;

const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 420px;
    overflow: hidden;
`;
